// If you are adding a term to the Source glossary,
// please add a member of the Data team to your PR

// Due to overlap between the name property of Screen events and the
// source property of UI, Track, and Operational events, we group
// them together in this file.
// https://hello.atlassian.net/wiki/spaces/MEASURE/pages/134329599/Analytics+Lesson+1+-+Event+Types

// More information on naming screens:
// https://hello.atlassian.net/wiki/spaces/MEASURE/pages/134329341/Screen+Events

// Inline dialogs (popovers) are components that pop up over the UI and provides additional functionality that involves user interaction.
const inlineDialogs = {
  aboutThisBoardAdminListInlineDialog: 'aboutThisBoardAdminListInlineDialog',
  actionableTaskInlineDialog: 'actionableTaskInlineDialog',
  addListInlineDialog: 'addListInlineDialog',
  addToEnterpriseInlineDialog: 'addToEnterpriseInlineDialog',
  adminListInlineDialog: 'adminListInlineDialog',
  allBoardMembersInlineDialog: 'boardMembersInlineDialog',
  attachmentGetPowerUpInlineDialog: 'attachmentGetPowerUpInlineDialog',
  attachmentsGetPowerUpUpgradeInlineDialog:
    'attachmentsGetPowerUpUpgradeInlineDialog',
  autoEnableCalendarInlineDialog: 'autoEnableCalendarInlineDialog',
  avatarChangeInlineDialog: 'avatarChangeInlineDialog',
  boardBackgroundPromptInlineDialog: 'boardBackgroundPromptInlineDialog',
  boardMenuDrawerEnterpriseUpsellInlineDialog:
    'boardMenuDrawerEnterpriseUpsellInlineDialog',
  boardsMenuInlineDialog: 'boardsMenuInlineDialog',
  cardComposerLabelSelectorInlineDialog:
    'cardComposerLabelSelectorInlineDialog',
  cardComposerMemberSelectorInlineDialog:
    'cardComposerMemberSelectorInlineDialog',
  cardComposerMenuInlineDialog: 'cardComposerMenuInlineDialog',
  cardComposerPositionSelectorInlineDialog:
    'cardComposerPositionSelectorInlineDialog',
  changeBoardOrganizationInlineDialog: 'changeBoardOrganizationInlineDialog',
  boardSidebarPupInlineDialog: 'boardSidebarPupInlineDialog',
  changeBoardPermissionsInlineDialog: 'changeBoardPermissionsInlineDialog',
  changeBoardVisibilityInlineDialog: 'changeBoardVisibilityInlineDialog',
  changeCreditCardInlineDialog: 'changeCreditCardInlineDialog',
  changeEmailInlineDialog: 'changeEmailInlineDialog',
  changeEmailSubmittedInlineDialog: 'changeEmailSubmittedInlineDialog',
  changeEmailWarningInlineDialog: 'changeEmailWarningInlineDialog',
  changeInvoiceDetailsInlineDialog: 'changeInvoiceDetailsInlineDialog',
  changeOrgInlineDialog: 'changeOrgInlineDialog',
  copyCardInlineDialog: 'copyCardInlineDialog',
  copyBoardInlineDialog: 'copyBoardInlineDialog',
  copyListInlineDialog: 'copyListInlineDialog',
  changeTeamAvatarDialog: 'changeTeamAvatarDialog',
  createCardFromTemplateInlineDialog: 'createCardFromTemplateInlineDialog',
  createMultipleCardsInlineDialog: 'createMultipleCardsInlineDialog',
  datePickerInlineDialog: 'datePickerInlineDialog',
  emailHygieneRemoveSecondaryEmailsInlineDialog:
    'emailHygieneRemoveSecondaryEmailsInlineDialog',
  emailHygieneChangeEmailInlineDialog: 'emailHygieneChangeEmailInlineDialog',
  emailToBoardSettingsInlineDialog: 'emailToBoardSettingsInlineDialog',
  exportBoardInlineDialog: 'exportBoardInlineDialog',
  inlineCardComposerInlineDialog: 'inlineCardComposerInlineDialog',
  inlineListComposerInlineDialog: 'inlineListComposerInlineDialog',
  inviteToBoardInlineDialog: 'inviteToBoardInlineDialog',
  linkCopyInlineDialog: 'linkCopyInlineDialog',
  listLimitInlineDialog: 'listLimitInlineDialog',
  listMenuInlineDialog: 'listMenuInlineDialog',
  miniProfileInlineDialog: 'miniProfileInlineDialog',
  moonshotWelcomeInlineDialog: 'moonshotWelcomeInlineDialog',
  moveAllCardsInListInlineDialog: 'moveAllCardsInListInlineDialog',
  newBoardHelperAddOrganizationInlineDialog:
    'newBoardHelperAddOrganizationInlineDialog',
  printAndExportBoardInlineDialog: 'printAndExportBoardInlineDialog',
  pupAttachmentSectionInlineDialog: 'pupAttachmentSectionInlineDialog',
  pupCardBackSectionInlineDialog: 'pupCardBackSectionInlineDialog',
  pupConfigurationInlineDialog: 'pupConfigurationInlineDialog',
  pupPopupInlineDialog: 'pupPopupInlineDialog',
  reactionSelectorInlineDialog: 'reactionSelectorInlineDialog',
  searchInlineDialog: 'searchInlineDialog',
  setPrimaryEmailInlineDialog: 'setPrimaryEmailInlineDialog',
  shareTemplateInlineDialog: 'shareTemplateInlineDialog',
  switchToYearlyBillingInlineDialog: 'switchToYearlyBillingInlineDialog',
  teamTemplatePicker: 'teamTemplatePicker',
  votingSettingsInlineDialog: 'votingSettingsInlineDialog',
} as const;

// Modals are components that are independently brought to the foreground while
// dimming out the rest of the experience. They are NOT popovers!
const modals = {
  boardPowerUpDirectoryModal: 'boardPowerUpDirectoryModal',
  closedBoardsModal: 'closedBoardsModal',
  createBoardModal: 'createBoardModal',
  createFreeTeamModal: 'createFreeTeamModal',
  freeTeamRestrictionModal: 'freeTeamRestrictionModal',
} as const;

// Drawers are components that slide in from the side and take control of the
// screen, like the boards menu on the left.
const drawers = {
  boardsDrawer: 'boardsDrawer',
  boardMenuDrawer: 'boardMenuDrawer',
  boardSidebarDrawer: 'boardSidebarDrawer', // Deprecated, use a boardMenuDrawerScreen or the generic `boardMenuDrawer`
} as const;

// Banners are commonly displayed between the app header and the board header
const banners = {
  atlassianAccountMigrationBanner: 'atlassianAccountMigrationBanner',
  atlassianAccountOnboardingBanner: 'atlassianAccountOnboardingBanner',
  browserWillBeUnsupportedBanner: 'browserWillBeUnsupportedBanner',
  emailHygieneBanner: 'emailHygieneBanner',
  emailVerificationBanner: 'emailVerificationBanner',
  enterpriseDeprovisioningBanner: 'enterpriseDeprovisioningBanner',
  enterpriseLicenseBanner: 'enterpriseLicenseBanner',
  personalBoardsOwnershipBanner: 'personalBoardsOwnershipBanner',
  invitedBanner: 'invitedBanner',
  loggedInPublicBoardBanner: 'loggedInPublicBoardBanner',
  newLocaleBanner: 'newLocaleBanner',
  overdueBanner: 'overdueBanner',
  setPasswordBanner: 'setPasswordBanner',
} as const;

// Screens are pages or tabs of an experience
const screens = {
  aaMigrationWizardCheckInboxScreen: 'aaMigrationWizardCheckInboxScreen',
  aaMigrationWizardLogInScreen: 'aaMigrationWizardLogInScreen',
  aaMigrationWizardCreateScreen: 'aaMigrationWizardCreateScreen',
  archiveScreen: 'archiveScreen',
  boardDirectoryScreen: 'boardDirectoryScreen',
  boardDirectoryFeaturedScreen: 'boardDirectoryFeaturedScreen',
  boardDirectoryPowerUpDetailsScreen: 'boardDirectoryPowerUpDetailsScreen',
  boardMenuDrawerAboutScreen: 'boardMenuDrawerAboutScreen',
  boardMenuDrawerActivityScreen: 'boardMenuDrawerActivityScreen',
  boardMenuDrawerArchiveScreen: 'boardMenuDrawerArchiveScreen',
  boardMenuDrawerBackgroundScreen: 'boardMenuDrawerBackgroundScreen',
  boardMenuDrawerBackgroundColorsScreen:
    'boardMenuDrawerBackgroundColorsScreen',
  boardMenuDrawerBackgroundPhotosScreen:
    'boardMenuDrawerBackgroundPhotosScreen',
  boardMenuDrawerCollectionsScreen: 'boardMenuDrawerCollectionsScreen',
  boardMenuDrawerDefaultScreen: 'boardMenuDrawerDefaultScreen',
  boardMenuDrawerFilterScreen: 'boardMenuDrawerFilterScreen',
  boardMenuDrawerMoreScreen: 'boardMenuDrawerMoreScreen',
  boardMenuDrawerSettingsScreen: 'boardMenuDrawerSettingsScreen',
  boardScreen: 'boardScreen', // Use for board
  boardsScreen: 'boardsScreen',
  cardDetailScreen: 'cardDetailScreen', // Use for cardback
  cardLabelsScreen: 'cardLabelsScreen',
  closeBoardScreen: 'closeBoardScreen', // After closing a board, before deleting
  emailHygieneAtlassianTrelloScreen: 'emailHygieneAtlassianTrelloScreen',
  emailHygieneAaMigrationScreen: 'emailHygieneAaMigrationScreen',
  emailHygieneConfirmEmailScreen: 'emailHygieneConfirmEmailScreen',
  emailHygieneChooseEmailScreen: 'emailHygieneChooseEmailScreen',
  emailHygieneClaimableWarningScreen: 'emailHygieneClaimableWarningScreen',
  emailHygieneConfirmationSentScreen: 'emailHygieneConfirmationSentScreen',
  emailHygieneErrorScreen: 'emailHygieneErrorScreen',
  emailHygieneThanksScreen: 'emailHygieneThanksScreen',
  homeScreen: 'homeScreen',
  iframeIO: 'iframeIO',
  instantSearchScreen: 'instantSearchScreen',
  labelEditScreen: 'labelEditScreen',
  memberBillingScreen: 'memberBillingScreen',
  memberHomeScreen: 'memberHomeScreen',
  moonshotBCFreeTrialUpsellScreen: 'moonshotBCFreeTrialUpsellScreen',
  moonshotCreateTeamScreen: 'moonshotCreateTeamScreen',
  moonshotInviteMembersScreen: 'moonshotInviteMembersScreen',
  moonshotPaymentScreen: 'moonshotPaymentScreen',
  moonshotPlanSelectionScreen: 'moonshotPlanSelectionScreen',
  moonshotSignupScreen: 'moonshotSignupScreen',
  newUserOrientationScreen: 'newUserOrientationScreen',
  nonEnterpriseTeamScreen: 'nonEnterpriseTeamScreen',
  organizationBoardsScreen: 'organizationBoardsScreen',
  powerUpsScreen: 'powerUpsScreen',
  profileAndVisibilitySettingsScreen: 'profileAndVisibilitySettingsScreen',
  publicDirectoryAllCategoriesScreen: 'publicDirectoryAllCategoriesScreen',
  publicDirectoryCategoryScreen: 'publicDirectoryCategoryScreen',
  publicDirectoryHomeScreen: 'publicDirectoryHomeScreen',
  publicDirectoryPowerUpDetailsScreen: 'publicDirectoryPowerUpDetailsScreen',
  publicDirectoryScreen: 'publicDirectoryScreen',
  publicDirectorySearchResultsScreen: 'publicDirectorySearchResultsScreen',
  publicPowerUpAddToBoardScreen: 'publicPowerUpAddToBoardScreen',
  publicPowerUpDetailsScreen: 'publicPowerUpDetailsScreen',
  pupAttachmentSuggestionSection: 'pupAttachmentSuggestionSection',
  savedSearchesScreen: 'savedSearchesScreen',
  savedSearchScreen: 'savedSearchScreen',
  searchHelpScreen: 'searchHelpScreen',
  searchResultsScreen: 'searchResultsScreen',
  shortcutsScreen: 'shortcutsScreen',
  sortListInlineDialog: 'sortListInlineDialog',
  teamBillingScreen: 'teamBillingScreen',
  teamScreen: 'teamScreen',
  teamMembersScreen: 'teamMembersScreen',
  teamSettingsScreen: 'teamSettingsScreen',
  templateCategoryScreen: 'templateCategoryScreen',
  templateGalleryScreen: 'templateGalleryScreen',
  templateStoryPageScreen: 'templateStoryPageScreen',
  orgProfileEditScreen: 'orgProfileEditScreen',
  unknownScreen: 'unknownScreen',
} as const;

const otherSources = {
  '@trello/feature-flag-client': '@trello/feature-flag-client',
  addPowerUpButton: 'addPowerUpButton',
  appHeader: 'appHeader', // The global Trello header
  appStartup: 'appStartup',
  atlassianSwitcherHeaderButton: 'atlassianSwitcherHeaderButton',
  atlassianAccountOnboardingOverlay: 'atlassianAccountOnboardingOverlay',
  atlassianSwitcher: 'atlassianSwitcher',
  boardDirectory: 'boardDirectory',
  boardDirectorySearch: 'boardDirectorySearch',
  boardMap: 'boardMap',
  cardLabelFilter: 'cardLabelFilter',
  crossFlowOverlay: 'crossFlowOverlay',
  closeBoardLink: 'closeBoardLink',
  globalErrorHandler: 'globalErrorHandler',
  'lib:pluginBehavioralAnalytics': 'lib:pluginBehavioralAnalytics',
  'lib:featureFlagWebClient': 'lib:featureFlagWebClient',
  listFooter: 'listFooter',
  listHeader: 'listHeader',
  newUserOrientationCard: 'newUserOrientationCard',
  onboardingDialog: 'onboardingDialog',
  powerUpCard: 'powerUpCard',
  pupCardBadges: 'pupCardBadges',
  stickerHoverView: 'stickerHoverView',
  stickerEditView: 'stickerEditView',
  templates: 'templates',
  templatesTemplateCategory: 'templatesTemplateCategory',
  templatesTemplateStory: 'templatesTemplateStory',
  templatesTemplatesFeatured: 'templatesTemplatesFeatured',
  // This is current being used for the generic global error handler. The intention is to eventually replace 'unknown'
  // with a source automatically determined based on the URL. Using 'unknown' should be avoided wherever possible.
  unknown: 'unknown',
} as const;

export const Source = {
  ...inlineDialogs,
  ...modals,
  ...drawers,
  ...banners,
  ...screens,
  ...otherSources,
} as const;

export type SourceType = typeof Source[keyof typeof Source];

export const isValidSource = (s: SourceType) => {
  const validSources = Object.values(Source);
  return validSources.includes(s);
};
